import {
    ButtonGradientPrimary,
    DraggableList,
    Pills,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { ActionMenu, type ActionMenuItem } from '../ActionMenu';
import { ArrowRightIcon, PencilIcon, PlusIcon, PowerIcon, Trash2Icon } from '../icons';
import {
    competencyQuestionTypes,
    createEmptyQuestion,
    hasMeaningfulLabel,
    isQuestionActive,
    sanitizeQuestionsForSave,
} from './competencyQuestion';
import { MoveQuestionModal } from './MoveQuestionModal';

import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { DraggableItem, OptionProps } from '@amelio/ui-library';

import { useWatchCompetencyGroups } from '@/api/competency-services/competency';
import {
    useGetCompetencyQuestions,
    useMoveCompetencyQuestion,
    useSaveCompetencyQuestions,
} from '@/api/competency-services/competency-question';
import { userInstanceAtom } from '@/atoms/userDetails';
import { GripVerticalIcon } from '@/components/GripVerticalIcon/GripVerticalIcon';
import { ReviewQuestionStatus } from '@/constants/questionnaire/Question';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { QuestionForm } from '@/pages/shared/QuestionnaireCreation/components/QuestionForm/QuestionForm';
import { showSuccessToast } from '@/plugins/Toast';
import { getTextByLanguage } from '@/utils/localization';

type CompetencyQuestionsEditorProps = {
    competencyId: string;
    competencyName: string;
    isOpen: boolean;
};

export const CompetencyQuestionsEditor = ({
    competencyId,
    competencyName,
    isOpen,
}: CompetencyQuestionsEditorProps) => {
    const { t } = useAmelioTranslations();
    const user = useAtomValue(userInstanceAtom);
    const firstNamePlaceholder = user?.firstName ?? '';
    const language = useSelectedAppLanguage();
    const { handleError } = useError();
    const { data, isFetching, isError } = useGetCompetencyQuestions(competencyId, isOpen);
    const { mutateAsync: saveQuestions } = useSaveCompetencyQuestions();
    const { data: competencyGroups } = useWatchCompetencyGroups();
    const { mutateAsync: moveQuestion, isPending: isMovingQuestion } = useMoveCompetencyQuestion();

    const [questions, setQuestions] = useState<ReviewQuestion[]>([]);
    const [questionToMove, setQuestionToMove] = useState<ReviewQuestion | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [newQuestionIds, setNewQuestionIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!data) {
            return;
        }

        if (editingId === null) {
            setQuestions(data);

            return;
        }

        setQuestions((current) => {
            const knownIds = new Set(current.map((question) => question.id));
            const incoming = data.filter((question) => !knownIds.has(question.id));

            return incoming.length > 0 ? [...current, ...incoming] : current;
        });
    }, [data, editingId]);

    const persist = async (
        nextQuestions: ReviewQuestion[],
        updateOptimistically = true,
    ): Promise<boolean> => {
        const previous = questions;

        if (updateOptimistically) {
            setQuestions(nextQuestions);
        }

        try {
            await saveQuestions({
                competencyId,
                payload: { questions: sanitizeQuestionsForSave(nextQuestions) },
            });
            showSuccessToast({ message: t('competencies.questions.saved') });

            return true;
        } catch (error) {
            setQuestions(previous);
            handleError({ error });

            return false;
        }
    };

    const forgetNewQuestion = (id: string | null) => {
        if (!id) {
            return;
        }

        setNewQuestionIds((current) => {
            if (!current.has(id)) {
                return current;
            }

            const next = new Set(current);
            next.delete(id);

            return next;
        });
    };

    const onQuestionChange = (question: ReviewQuestion) => {
        setQuestions((current) =>
            current.map((item) => (item.id === question.id ? question : item)),
        );
    };

    const onSaveQuestion = async (question: ReviewQuestion) => {
        const exists = questions.some((item) => item.id === question.id);
        const nextQuestions = exists
            ? questions.map((item) => (item.id === question.id ? question : item))
            : [...questions, question];

        const succeeded = await persist(nextQuestions);

        if (succeeded) {
            forgetNewQuestion(question.id ?? null);
            setEditingId(null);
        }
    };

    const onCancelEdit = () => {
        setQuestions((current) =>
            current.filter((item) => item.id !== editingId || hasMeaningfulLabel(item.label)),
        );
        forgetNewQuestion(editingId);
        setEditingId(null);
    };

    const onAddQuestion = () => {
        const draft = createEmptyQuestion(competencyId, questions.length, language);
        setQuestions((current) => [...current, draft]);
        setNewQuestionIds((current) => {
            if (!draft.id) {
                return current;
            }

            return new Set(current).add(draft.id);
        });
        setEditingId(draft.id ?? null);
    };

    const onToggleStatus = (question: ReviewQuestion) => {
        const nextStatus = isQuestionActive(question)
            ? ReviewQuestionStatus.INACTIVE
            : ReviewQuestionStatus.ACTIVE;

        void persist(
            questions.map((item) =>
                item.id === question.id ? { ...item, status: nextStatus } : item,
            ),
            false,
        );
    };

    const onDeleteQuestion = (question: ReviewQuestion) =>
        void persist(questions.filter((item) => item.id !== question.id));

    const onReorderQuestions = (items: DraggableItem<ReviewQuestion>[]) =>
        void persist(items.map((item) => item.data));

    const moveTargetOptions: OptionProps[] = (competencyGroups ?? []).flatMap((group) =>
        group.competencies
            .filter((competency) => competency.id !== competencyId)
            .map((competency) => ({
                id: competency.id,
                value: competency.id,
                label: `${getTextByLanguage(group.name, language)} — ${getTextByLanguage(
                    competency.name,
                    language,
                )}`,
            })),
    );

    const onConfirmMoveQuestion = async (targetCompetencyId: string) => {
        if (!questionToMove?.id) {
            return;
        }

        try {
            await moveQuestion({
                questionId: questionToMove.id,
                targetCompetencyId,
                sourceCompetencyId: competencyId,
            });
            setQuestions((current) => current.filter((item) => item.id !== questionToMove.id));
            setQuestionToMove(null);
            showSuccessToast({ message: t('competencies.questions.move.success') });
        } catch (error) {
            handleError({ error });
        }
    };

    const buildActions = (question: ReviewQuestion): ActionMenuItem[] => [
        {
            id: 'edit',
            label: t('common.edit'),
            icon: <PencilIcon className={'h-4 w-4'} />,
            onClick: () => setEditingId(question.id ?? null),
        },
        {
            id: 'move',
            label: t('competencies.questions.move.action'),
            icon: <ArrowRightIcon className={'h-4 w-4'} />,
            onClick: () => setQuestionToMove(question),
        },
        {
            id: 'toggle-status',
            label: isQuestionActive(question)
                ? t('competencies.questions.deactivate')
                : t('competencies.questions.activate'),
            icon: <PowerIcon className={'h-4 w-4'} />,
            onClick: () => onToggleStatus(question),
        },
        {
            id: 'delete',
            label: t('common.delete'),
            icon: <Trash2Icon className={'h-4 w-4'} />,
            danger: true,
            onClick: () => onDeleteQuestion(question),
        },
    ];

    const hasQuestions = questions.length > 0;
    const hasActiveQuestions = questions.some((q) => q.status === 'Active');

    const draggableQuestions: DraggableItem<ReviewQuestion>[] = questions
        .filter((question): question is ReviewQuestion & { id: string } => Boolean(question.id))
        .map((question) => ({ id: question.id, data: question }));

    const editingQuestion = editingId
        ? (questions.find((question) => question.id === editingId) ?? null)
        : null;

    return (
        <div className={'cmp-questions-indent cmp-stack-6 mt-6'}>
            {hasQuestions && (
                <h4 className={'text-navy text-body-lg mb-6 font-medium'}>
                    {t('competencies.questions.title')}
                </h4>
            )}

            {isFetching && !hasQuestions && (
                <p className={'text-md text-base-400'}>{t('competencies.questions.empty')}</p>
            )}

            {isError && (
                <p className={'text-error text-sm'}>{t('competencies.questions.loadError')}</p>
            )}

            {!isFetching && !isError && !hasActiveQuestions && (
                <div
                    className={
                        'mb-4 flex items-start gap-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4'
                    }
                    data-testid={`competency-standard-question-${competencyId}`}
                >
                    <svg
                        className={'mt-0.5 h-5 w-5 flex-shrink-0 text-[#64748B]'}
                        viewBox={'0 0 24 24'}
                        fill={'none'}
                        stroke={'currentColor'}
                        strokeWidth={1.5}
                        strokeLinecap={'round'}
                        strokeLinejoin={'round'}
                    >
                        <path d={'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'} />
                    </svg>

                    <p className={'text-sm leading-relaxed text-[#475467]'}>
                        <Trans
                            i18nKey={'competencies.questions.standardQuestionNotice'}
                            values={{
                                firstNamePlaceholder,
                                name: competencyName,
                            }}
                            components={{
                                bold: <strong className={'font-semibold text-[#1E293B]'} />,
                            }}
                        />
                    </p>

                    <div className={'top-5 right-5'}>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type={'button'}
                                        className={
                                            'text-base-400/60 hover:text-base-400 transition-colors'
                                        }
                                    >
                                        <svg
                                            className={'h-5 w-5'}
                                            viewBox={'0 0 24 24'}
                                            fill={'none'}
                                            stroke={'currentColor'}
                                            strokeWidth={2}
                                            strokeLinecap={'round'}
                                            strokeLinejoin={'round'}
                                        >
                                            <circle
                                                cx={'12'}
                                                cy={'12'}
                                                r={'10'}
                                            />
                                            <path d={'M12 16v-4'} />
                                            <path d={'M12 8h.01'} />
                                        </svg>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent
                                    className={'max-w-xs'}
                                    side={'top'}
                                >
                                    <p>{t('competencies.questions.fallbackQuestionInfo')}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            )}

            {hasQuestions && (
                <DraggableList
                    items={draggableQuestions}
                    onReorder={onReorderQuestions}
                    gap={24}
                    dataTestId={`competency-questions-${competencyId}`}
                    editorSlotAfterId={editingId ?? undefined}
                    renderEditorSlot={
                        editingQuestion
                            ? () => (
                                  <div
                                      className={
                                          'rounded-2xl border border-gray-200 bg-white shadow-[0_8px_30px_rgba(40,48,77,0.06)]'
                                      }
                                  >
                                      <QuestionForm
                                          key={editingQuestion.id}
                                          mode={
                                              editingQuestion.id &&
                                              newQuestionIds.has(editingQuestion.id)
                                                  ? 'add'
                                                  : 'edit'
                                          }
                                          question={editingQuestion}
                                          allowedTypes={[...competencyQuestionTypes]}
                                          allowQuestionTypeEdit={true}
                                          hideAdvancedOptions={true}
                                          requireLabel={true}
                                          showQuestionHelp={true}
                                          validateOnSave={true}
                                          questionDefaults={{
                                              satisfactionLabels: [
                                                  t(
                                                      'competencies.questions.defaultSatisfaction.best',
                                                  ),
                                                  t(
                                                      'competencies.questions.defaultSatisfaction.high',
                                                  ),
                                                  t(
                                                      'competencies.questions.defaultSatisfaction.mid',
                                                  ),
                                                  t(
                                                      'competencies.questions.defaultSatisfaction.low',
                                                  ),
                                                  t(
                                                      'competencies.questions.defaultSatisfaction.worst',
                                                  ),
                                              ],
                                              scaleFromLabel: t(
                                                  'competencies.questions.defaultScaleFrom',
                                              ),
                                              scaleToLabel: t(
                                                  'competencies.questions.defaultScaleTo',
                                              ),
                                              hideOtherOption: true,
                                              emptyOptionCount: 3,
                                          }}
                                          onQuestionChange={onQuestionChange}
                                          onSave={onSaveQuestion}
                                          onCancel={onCancelEdit}
                                      />
                                  </div>
                              )
                            : undefined
                    }
                    disabledItemIds={editingId ? [editingId] : undefined}
                    renderItem={(item) => {
                        if (item.id === editingId) {
                            return null;
                        }

                        const question = item.data;

                        return (
                            <QuestionRow
                                question={question}
                                label={getTextByLanguage(question.label, language)}
                                actions={buildActions(question)}
                                activeLabel={t('competencies.questions.active')}
                                inactiveLabel={t('competencies.questions.inactive')}
                                onEdit={() => setEditingId(question.id ?? null)}
                            />
                        );
                    }}
                />
            )}

            {!editingId && (
                <ButtonGradientPrimary
                    size={'md'}
                    onClick={onAddQuestion}
                    leftIcon={<PlusIcon className={'h-5 w-5'} />}
                    dataTestId={`competency-question-add-${competencyId}`}
                >
                    {t('competencies.questions.add')}
                </ButtonGradientPrimary>
            )}

            {questionToMove && (
                <MoveQuestionModal
                    questionLabel={getTextByLanguage(questionToMove.label, language)}
                    competencyOptions={moveTargetOptions}
                    isSubmitting={isMovingQuestion}
                    onConfirm={(targetCompetencyId) =>
                        void onConfirmMoveQuestion(targetCompetencyId)
                    }
                    onCancel={() => setQuestionToMove(null)}
                />
            )}
        </div>
    );
};

type QuestionRowProps = {
    question: ReviewQuestion;
    label: string;
    actions: ActionMenuItem[];
    activeLabel: string;
    inactiveLabel: string;
    onEdit: () => void;
};

const QuestionRow = ({
    question,
    label,
    actions,
    activeLabel,
    inactiveLabel,
    onEdit,
}: QuestionRowProps) => {
    const active = isQuestionActive(question);

    return (
        <div className={'flex flex-col gap-3 sm:flex-row sm:items-center'}>
            <span className={'text-base-300 flex-shrink-0 cursor-move'}>
                <GripVerticalIcon className={'h-6 w-6'} />
            </span>

            <div
                className={
                    'flex flex-col gap-3 rounded-lg bg-gray-100 px-3 py-3 transition-colors hover:bg-gray-200 sm:flex-1 sm:flex-row sm:items-center sm:gap-3'
                }
            >
                <button
                    type={'button'}
                    onClick={onEdit}
                    className={
                        'text-navy text-md flex-1 cursor-pointer text-left font-normal hover:underline'
                    }
                    data-testid={`competency-question-edit-${question.id}`}
                >
                    {label}
                </button>

                <div className={'ml-auto flex flex-row items-center gap-3'}>
                    <Pills
                        variant={active ? 'activeLight' : 'inactiveLight'}
                        status={active ? 'active' : 'inactive'}
                        size={'sm'}
                    >
                        {active ? activeLabel : inactiveLabel}
                    </Pills>

                    <ActionMenu
                        items={actions}
                        dataTestId={`competency-question-actions-${question.id}`}
                    />
                </div>
            </div>
        </div>
    );
};
